import { useCallback, useMemo, useState, useEffect } from 'react';
import { translations } from './translations';
import { getCurrentLanguageDirect, Language } from './index';

export const useTranslation = () => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(getCurrentLanguageDirect());

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: Language }>) => {
      setCurrentLanguage(event.detail.language);
    };

    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
    };
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const translation = translations[key];
    
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }

    let text = translation[currentLanguage] || translation.fr || key;

    // Replace parameters if provided
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{${param}}`, 'g'), String(value));
      });
    }

    return text;
  }, [currentLanguage]);

  const tWithFallback = useCallback((key: string, fallback?: string): string => {
    const translation = translations[key];
    
    if (!translation) {
      return fallback || key;
    }

    return translation[currentLanguage] || translation.fr || fallback || key;
  }, [currentLanguage]);

  const getLanguageInfo = useCallback(() => {
    return {
      current: currentLanguage,
      isFrench: currentLanguage === 'fr',
      isEnglish: currentLanguage === 'en'
    };
  }, [currentLanguage]);

  return {
    t,
    tWithFallback,
    currentLanguage,
    getLanguageInfo
  };
};
